// SCADA sayfası silme — ÇİFT ONAY.
//
// Kullanıcı isteği: "silme işlemi çift onay ile olsun, sistem yöneticisi
// yapabilsin sadece".
//
// Neden çift onay şart: bir sayfayı silmek ona bağlı TÜM etiketleri ve
// ölçümleri götürüyor. Ekran görüntüsündeki "TESİS" sayfası 17 etiket taşıyor;
// aylık zaman serisi tek tıkla gidebilir. Tek onay kutusu, alışkanlıkla
// "Tamam"a basmayı engellemiyor.
//
// İki adım:
//   1) Ne kaybedileceğini RAKAMLA gör (sunucudan: etiket · ölçüm · paket · anahtar)
//   2) Sayfa KODUNU elle yaz — kas hafızasıyla geçilemeyen kapı
//
// Yetki: uç `Entegrasyon` YAZMA istiyor ve matriste bu hak yalnız Tesis
// Sorumlusu'nda (operatör/laborant salt-okunur, muhasebe hiç görmüyor). Düğme
// de `yazabilir` olmadan çıkmıyor — ayrı bir rol kontrolü gerekmiyor.
//
// ÖZET ALINAMAZSA SİLME KAPALI. Önceki sürüm `isError`'ı hiç okumuyordu:
// sorgu 500/403/ağ hatası verdiğinde `data` undefined kalıyor, tablo
// `?? 0` sayesinde "0 · 0 · 0" basıyor ve `isLoading` false olduğu için
// düğme AÇILIYORDU. Yani "kayıp yok" ile "ne kaybedeceğini bilmiyorum"
// ekranda birebir aynı görünüyordu — yıkıcı bir işlemin önizlemesinde
// kabul edilemez.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { tr, trTarihSaat } from '../../lib/trFormat'
import { entegrasyon, type SayfaSatiri } from './api'

export default function SayfaSilDrawer({
  sayfa,
  silindi,
}: {
  sayfa: SayfaSatiri
  /** Silme başarılıysa çağrılır — seçimi temizlemek çağıranın işi. */
  silindi?: () => void
}) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const [yazilan, setYazilan] = useState('')

  const {
    data: ozet,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['entegrasyon-sayfa-silme-ozeti', sayfa.id],
    queryFn: () => entegrasyon.sayfaSilmeOzeti(sayfa.id),
  })

  // İkinci kapı: kod BİREBİR yazılmalı. Boşluk toleransı var, harf değişimi yok
  // — "genel_bakis" ile "genel bakis" ayrı sayfalar olabilir.
  const kodDogru = yazilan.trim() === sayfa.sayfaKodu
  const anahtarlar = ozet?.anahtarlar ?? []

  const sil = useMutation({
    mutationFn: () => entegrasyon.sayfaSil(sayfa.id),
    onSuccess: (s) => {
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-kaynaklar'] })
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-kaynak'] })
      // Etiket listesi ekranının gerçek anahtarı bu; eski kod var olmayan bir
      // ['entegrasyon-etiketler'] anahtarını tazeliyordu ve silinen etiketler
      // ekranda durmaya devam ediyordu.
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-tum-etiketler'] })
      // Paketlerin sayfa bağı koptu — listedeki sayfa kodu etiketi bayatladı.
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-paketler'] })
      // Sayfa artık yok: detay sorgusu 404'e düşmesin diye önbellekten çıkar.
      kuyruk.removeQueries({ queryKey: ['entegrasyon-sayfa', sayfa.id] })
      kuyruk.removeQueries({ queryKey: ['entegrasyon-sayfa-silme-ozeti', sayfa.id] })

      toast(
        `${s.sayfaKodu} silindi — ${tr(s.etiket, 0)} etiket · ${tr(s.olcum, 0)} ölçüm kaldırıldı` +
          (s.paketBagiKopan > 0
            ? ` · ${tr(s.paketBagiKopan, 0)} ham paket korundu`
            : '') +
          (s.anahtarBagiKopan > 0
            ? ` · ${tr(s.anahtarBagiKopan, 0)} API anahtarı çalışmaya devam ediyor`
            : ''),
      )
      silindi?.()
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Sayfa silinemedi'),
  })

  return (
    <>
      <DrawerGovde>
        <div className="rule">
          <b>1 · Ne kaybedilecek</b>
          <span>
            Yalnız <b>bu ekrandan gelen okumalar</b> silinir. Aynı etiket başka
            ekranlarda da okunuyorsa etiket ve oradaki geçmişi yerinde kalır.
            Bu işlem geri alınamaz.
          </span>
        </div>

        {isLoading ? (
          <div className="cell-sub" style={{ padding: '8px 0' }}>Özet alınıyor…</div>
        ) : isError ? (
          <div className="rule">
            <b>Özet alınamadı — silme kapalı</b>
            <span>
              {error instanceof ApiHatasi ? error.message : 'Sunucuya ulaşılamadı.'}{' '}
              Neyin kaybedileceğini gösteremeden silmeye izin verilmiyor. Çekmeceyi
              kapatıp yeniden açın.
            </span>
          </div>
        ) : (
          <>
            <table className="grid">
              <tbody>
                <tr>
                  <td>Sayfa</td>
                  <td className="num">
                    <span className="kod">{sayfa.sayfaKodu}</span>
                  </td>
                </tr>
                {(ozet?.kalacakEtiket ?? 0) > 0 && (
                  <tr>
                    <td>
                      Kalacak etiket{' '}
                      <span className="cell-sub">(başka ekranlarda da okunuyor)</span>
                    </td>
                    <td className="num mono">{tr(ozet?.kalacakEtiket ?? 0, 0)}</td>
                  </tr>
                )}
                <tr>
                  <td>
                    Silinecek etiket
                    {(ozet?.bagliEtiket ?? 0) > 0 && (
                      <span className="cell-sub">
                        {' '}
                        ({tr(ozet!.bagliEtiket, 0)} tanesi bir üniteye/parametreye bağlı)
                      </span>
                    )}
                  </td>
                  <td className="num mono">{tr(ozet?.etiket ?? 0, 0)}</td>
                </tr>
                <tr>
                  <td>Silinecek ölçüm <span className="cell-sub">(en az)</span></td>
                  <td className="num mono">{tr(ozet?.olcum ?? 0, 0)}</td>
                </tr>
                <tr>
                  <td>
                    Ham paket <span className="cell-sub">(silinmez, bağı kopar)</span>
                  </td>
                  <td className="num mono">{tr(ozet?.paket ?? 0, 0)}</td>
                </tr>
                <tr>
                  <td>
                    API anahtarı <span className="cell-sub">(silinmez, bağı kopar)</span>
                  </td>
                  <td className="num mono">{tr(anahtarlar.length, 0)}</td>
                </tr>
                <tr>
                  <td>Son veri</td>
                  <td className="num mono">
                    {ozet?.sonVeri ? trTarihSaat(ozet.sonVeri) : '—'}
                  </td>
                </tr>
              </tbody>
            </table>

            {anahtarlar.length > 0 && (
              <div className="rule">
                <b>API anahtarları çalışmaya devam edecek</b>
                <span>
                  Bu sayfa için üretilmiş{' '}
                  {anahtarlar.map((a) => `${a.ad} (${a.onek}…)`).join(' · ')}{' '}
                  <b>iptal edilmiyor</b> — yalnız sayfa bağı kopuyor ve sayfa kodu
                  anahtarın üzerinde saklanıyor. İptal edilseydi ajan 401 alır ve
                  gönderdiği paketleri bir daha göndermezdi. Akışı gerçekten
                  durdurmak isterseniz anahtarı Anahtarlar sekmesinden ayrıca iptal
                  edin.
                </span>
              </div>
            )}

            {ozet?.otomatikDoner && (
              <div className="rule">
                <b>Sayfa geri gelebilir</b>
                <span>
                  Ajan bu sayfayı hâlâ gönderiyor (son 24 saatte veri geldi).
                  Silmek ajanı durdurmuyor — sayfa ilk pakette <b>boş olarak
                  yeniden açılır</b>. Kalıcı olarak kaldırmak için önce ajan
                  yapılandırmasından çıkarın.
                </span>
              </div>
            )}
          </>
        )}

        <div className="rule">
          <b>2 · Onaylamak için sayfa kodunu yazın</b>
          <span>
            Yanlışlıkla silmeyi engellemek için kod elle yazılmalı:{' '}
            <b>{sayfa.sayfaKodu}</b>
          </span>
        </div>

        <div className="fld">
          <label>Sayfa kodu</label>
          <input
            className="in mono"
            value={yazilan}
            placeholder={sayfa.sayfaKodu}
            autoComplete="off"
            spellCheck={false}
            disabled={isError}
            onChange={(e) => setYazilan(e.target.value)}
          />
          {yazilan.trim().length > 0 && !kodDogru && (
            <div className="fld-hint">Kod eşleşmiyor.</div>
          )}
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-ghost" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-dan"
          type="button"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={!kodDogru || sil.isPending || isLoading || isError}
          title={
            isError
              ? 'Silme özeti alınamadı — ne kaybedileceği bilinmeden silinemez'
              : kodDogru
                ? undefined
                : 'Onaylamak için sayfa kodunu yazın'
          }
          onClick={() => sil.mutate()}
        >
          {sil.isPending ? 'Siliniyor…' : 'Sayfayı ve verisini sil'}
        </button>
      </DrawerAltlik>
    </>
  )
}
